"use client"

import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import ContactForm from '../ui/ContactForm';
import { useTheme } from '../layout/ThemeProvider';
import Image from 'next/image';

/**
 * Componente da seção de contato com mapa do Google otimizado para performance
 * 
 * Otimizações implementadas:
 * 1. Carregamento tardio (lazy loading) usando IntersectionObserver
 * 2. Uso inicial de imagem estática da Google Maps Static API (mais leve)
 * 3. Carregamento do mapa interativo apenas mediante interação do usuário
 * 
 * Estas otimizações reduzem significativamente o JavaScript não utilizado,
 * melhorando o tempo de carregamento da página e a pontuação no PageSpeed.
 */
const ContactSection: React.FC = () => {
  const { theme } = useTheme();
  const [loadMap, setLoadMap] = useState(false);
  const [showInteractiveMap, setShowInteractiveMap] = useState(false);
  const [imageError, setImageError] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  // Coordenadas do mapa EXATAS
  const mapLat = "-22.82403";
  const mapLng = "-47.03492";
  const mapZoom = "14"; // Zoom reduzido para mostrar área maior
  const mapInteractiveZoom = "17"; // Zoom maior para o mapa interativo
  const mapWidth = "800";
  const mapHeight = "400";
  const mapMarker = `color:red|${mapLat},${mapLng}`;
  
  // URL do mapa estático SEM estilos customizados para teste (mantendo coordenadas corretas)
  const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${mapLat},${mapLng}&zoom=${mapZoom}&size=${mapWidth}x${mapHeight}&markers=${mapMarker}&scale=2&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY'}`;

  useEffect(() => {
    // Console log para diagnóstico da URL do mapa estático
    if (loadMap) {
      console.log('URL do mapa estático:', staticMapUrl);
    }

    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setLoadMap(true);
          observer.disconnect();
        }
      });
    }, options);

    // Armazenar a referência em uma variável para uso na função de limpeza
    const currentMapRef = mapRef.current;

    if (currentMapRef) {
      observer.observe(currentMapRef);
    }

    return () => {
      if (currentMapRef) {
        observer.unobserve(currentMapRef);
      }
    };
  }, [staticMapUrl, loadMap]);

  const handleMapClick = () => {
    setShowInteractiveMap(true);
  };

  return (
    <section 
      id="contato" 
      className={`py-24 ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-white'
      } relative overflow-hidden`}
      aria-labelledby="contact-heading"
    >
      {/* Background decorativo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <svg 
          className="absolute right-0 top-0 transform translate-x-1/2 -translate-y-1/4" 
          width="800" 
          height="800" 
          fill="none" 
          viewBox="0 0 800 800"
          aria-hidden="true"
        >
          <circle 
            cx="400" 
            cy="400" 
            r="400" 
            fill={theme === 'dark' ? "#1E40AF" : "#3B82F6"} 
            fillOpacity="0.05" 
          />
          <circle 
            cx="400" 
            cy="400" 
            r="300" 
            fill={theme === 'dark' ? "#1E40AF" : "#3B82F6"}
            fillOpacity="0.08" 
          />
        </svg>
        
        <svg 
          className="absolute left-0 bottom-0 transform -translate-x-1/2 translate-y-1/4" 
          width="800" 
          height="800" 
          fill="none" 
          viewBox="0 0 800 800"
          aria-hidden="true"
        >
          <circle 
            cx="400" 
            cy="400" 
            r="400" 
            fill={theme === 'dark' ? "#4F46E5" : "#818CF8"} 
            fillOpacity="0.05" 
          />
          <circle 
            cx="400" 
            cy="400" 
            r="300" 
            fill={theme === 'dark' ? "#4F46E5" : "#818CF8"}
            fillOpacity="0.08" 
          />
        </svg>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div 
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <div className={`inline-block px-3 py-1 text-sm font-semibold ${
            theme === 'dark' 
              ? 'text-blue-300 bg-blue-900/50' 
              : 'text-blue-800 bg-blue-100'
          } rounded-full mb-3`}>
            Fale Conosco
          </div>
          <h2 
            id="contact-heading"
            className={`text-3xl md:text-4xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-4`}
          >
            Entre em Contato
          </h2>
          <p className={`text-xl ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Tem alguma dúvida ou precisa de um orçamento? Preencha o formulário abaixo e nossa equipe entrará em contato o mais breve possível.
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 items-start">
          {/* Informações de contato */}
          <div 
            className={`md:col-span-1 ${
              theme === 'dark' 
                ? 'bg-gray-800 border border-gray-700' 
                : 'bg-white shadow-xl'
            } rounded-lg p-8`}
          >
            <h3 className={`text-xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            } mb-6`}>
              Informações de Contato
            </h3>
            
            <ul className="space-y-6">
              <li className="flex">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full ${
                  theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
                } flex items-center justify-center text-blue-600 mr-4`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h4 className={`font-medium ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  } mb-1`}>
                    Telefone
                  </h4>
                  <a 
                    href="tel:+551932060706" 
                    className={`${
                      theme === 'dark' ? 'text-gray-300 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
                    }`}
                  >
                    (19) 3206-0706
                  </a>
                </div>
              </li>
              
              {/* WhatsApp item */}
              <li className="flex">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full ${
                  theme === 'dark' ? 'bg-green-900/50' : 'bg-green-100'
                } flex items-center justify-center text-green-600 mr-4`}>
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <div>
                  <h4 className={`font-medium ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  } mb-1`}>
                    WhatsApp
                  </h4>
                  <a 
                    href="https://wa.me/5519994799400" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${
                      theme === 'dark' ? 'text-gray-300 hover:text-green-400' : 'text-gray-600 hover:text-green-600'
                    }`}
                  >
                    (19) 99479-9400
                  </a>
                </div>
              </li>
              
              <li className="flex">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full ${
                  theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
                } flex items-center justify-center text-blue-600 mr-4`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className={`font-medium ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  } mb-1`}>
                    Email
                  </h4>
                  <a 
                    href="mailto:contato@webimports.com.br" 
                    className={`${
                      theme === 'dark' ? 'text-gray-300 hover:text-blue-400' : 'text-gray-600 hover:text-blue-600'
                    }`}
                  >
                    contato@webimports.com.br
                  </a>
                </div>
              </li>
              
              <li className="flex">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full ${
                  theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
                } flex items-center justify-center text-blue-600 mr-4`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className={`font-medium ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  } mb-1`}>
                    Endereço
                  </h4>
                  <address className={`not-italic ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Rua Tapuru, 25<br />
                    Alpha Mall / Loja 26<br />
                    Alphaville, Campinas - SP, 13098-322
                  </address>
                </div>
              </li>
              
              {/* Horário de funcionamento */}
              <li className="flex">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full ${
                  theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
                } flex items-center justify-center text-blue-600 mr-4`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className={`font-medium ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  } mb-1`}>
                    Horário
                  </h4>
                  <p className={`${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Segunda - Sexta: 9h às 18h<br />
                    Sábado: 9h às 13h
                  </p>
                </div>
              </li>
            </ul>
            
            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
              <h4 className={`font-medium ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}>
                Siga-nos
              </h4>
              <div className="flex space-x-4">
                <motion.a 
                  whileHover={{ y: -3, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  href="#" 
                  className={`w-10 h-10 rounded-full ${
                    theme === 'dark' ? 'bg-gray-700 hover:bg-blue-600' : 'bg-gray-100 hover:bg-blue-600'
                  } flex items-center justify-center ${
                    theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-white'
                  } transition-colors`}
                  aria-label="Facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                  </svg>
                </motion.a>
                <motion.a 
                  href="https://www.instagram.com/webimports/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    theme === 'dark' ? 'bg-pink-900/30 text-pink-400 hover:bg-pink-900/50' : 'bg-pink-100 text-pink-600 hover:bg-pink-200'
                  } transition-colors duration-200`}
                  aria-label="Instagram da Web Imports"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                  </svg>
                </motion.a>
                <motion.a 
                  whileHover={{ y: -3, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  href="https://wa.me/5519994799400" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-full ${
                    theme === 'dark' ? 'bg-gray-700 hover:bg-green-600' : 'bg-gray-100 hover:bg-green-600'
                  } flex items-center justify-center ${
                    theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-white'
                  } transition-colors`}
                  aria-label="WhatsApp"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </motion.a>
              </div>
            </div>
          </div>
          
          {/* Formulário de contato */}
          <div className="md:col-span-2">
            <ContactForm />
          </div>
        </div>
        
        {/* Mapa */}
        <div className="mt-16">
          <div 
            ref={mapRef}
            className={`rounded-lg overflow-hidden shadow-xl h-96 ${
              theme === 'dark' ? 'border border-gray-700' : ''
            }`}
          >
            {!loadMap ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                <p className="text-gray-500 dark:text-gray-400">Carregando mapa...</p>
              </div>
            ) : showInteractiveMap ? (
              <div className="relative w-full h-full">
                <iframe 
                  title="Localização da empresa" 
                  src={`https://www.google.com/maps/embed/v1/place?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&q=${mapLat},${mapLng}&zoom=${mapInteractiveZoom}`}
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade" 
                  aria-label="Mapa mostrando a localização da empresa"
                ></iframe>
                {/* Logo no canto inferior */}
                <div className="absolute top-1/2 left-10 transform -translate-y-1/2 z-10 bg-white p-2 rounded-md shadow-md">
                  <div className="text-blue-600 font-bold text-sm">Web Imports</div>
                  <div className="text-xs text-gray-600">Rua Tapuru, 25</div>
                  <div className="text-xs text-gray-600">Alphamall - Loja 26</div>
                  <div className="text-xs text-gray-600">Campinas-SP</div>
                </div>
              </div>
            ) : (
              <div 
                className="w-full h-full relative"
                onClick={handleMapClick}
                style={{ cursor: 'pointer' }}
              >
                <div className="relative w-full h-full">
                  <Image 
                    src={staticMapUrl}
                    alt="Mapa da localização da Web Imports"
                    fill
                    sizes="(max-width: 768px) 100vw, 800px"
                    className="object-cover"
                    unoptimized={true}
                    priority={true}
                    onError={(e) => {
                      console.error('Erro ao carregar imagem do mapa:', e);
                      setImageError(true);
                    }}
                  />
                </div>
                {imageError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-200 dark:bg-gray-800 p-4">
                    <p className="text-red-500 mb-4">Erro ao carregar o mapa estático.</p>
                    <button 
                      onClick={handleMapClick}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Carregar mapa interativo
                    </button>
                  </div>
                )}
                {/* Logo no canto inferior */}
                {!imageError && (
                  <div className="absolute top-1/2 left-10 transform -translate-y-1/2 z-10 bg-white p-2 rounded-md shadow-md">
                    <div className="text-blue-600 font-bold text-sm">Web Imports</div>
                    <div className="text-xs text-gray-600">Rua Tapuru, 25</div>
                    <div className="text-xs text-gray-600">Alphamall - Loja 26</div>
                    <div className="text-xs text-gray-600">Campinas-SP</div>
                  </div>
                )}
                {/* Overlay com botão visível apenas se a imagem não tem erro */}
                {!imageError && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                    <div className={`px-4 py-2 rounded-lg ${
                      theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
                    } shadow-lg pointer-events-auto`}>
                      <span>Clique para mapa interativo</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;